// ABOUTME: Chart 1 for FamilyInsightsPage — heart day success rate trend line with benchmark dotted line
// ABOUTME: Supports multi-child view (one line per child), hover highlighting, and day drill-down on click

import React, { useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  MouseHandlerDataParam,
} from 'recharts';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import naluV1Theme from '../theme/theme.nalu-v1';
import { Child } from '../types/api';
import { getChildColor } from '../utils/childColors';
import { HeartDayDataPoint } from '../utils/insightsUtils';

interface ChildHeartDayData {
  child: Child;
  data: HeartDayDataPoint[];
}

interface HeartDayRateChartProps {
  childrenData: ChildHeartDayData[];
  allTimeAverages: Record<number, number>; // childId → 0–1 average
  isDayGranularity: boolean;
  granularity: 'day' | 'week' | 'month';
  startDate: string;
  endDate: string;
  onPointClick: (date: string) => void;
  helperTextDismissed?: boolean;
}

// Enumerate every calendar day from startDate to endDate as YYYY-MM-DD strings
function enumerateDays(startDate: string, endDate: string): string[] {
  const dates: string[] = [];
  const cur = new Date(startDate + 'T00:00:00');
  const end = new Date(endDate + 'T00:00:00');
  while (cur <= end) {
    const y = cur.getFullYear();
    const m = String(cur.getMonth() + 1).padStart(2, '0');
    const d = String(cur.getDate()).padStart(2, '0');
    dates.push(`${y}-${m}-${d}`);
    cur.setDate(cur.getDate() + 1);
  }
  return dates;
}

// Merge per-child series into a unified array keyed by period for Recharts
function buildChartData(
  childrenData: ChildHeartDayData[],
  startDate: string,
  endDate: string,
  isDayGranularity: boolean,
): { period: string; [key: string]: number | string }[] {
  // For day granularity, enumerate all days so gaps show as 0% rather than being skipped
  const periodSet = new Set<string>();
  if (isDayGranularity) {
    enumerateDays(startDate, endDate).forEach(d => periodSet.add(d));
  } else {
    childrenData.forEach(({ data }) => data.forEach(d => periodSet.add(d.period)));
  }
  const periods = Array.from(periodSet).sort();

  return periods.map(period => {
    const point: { period: string; [key: string]: number | string } = { period };
    childrenData.forEach(({ child, data }) => {
      const childId = (child.id ?? child.childId) as number;
      const found = data.find(d => d.period === period);
      point[`child_${childId}`] = found !== undefined ? Math.round(found.successRate * 100) : 0;
    });
    return point;
  });
}

function formatAxisLabel(period: string): string {
  if (!period) return '';
  try {
    const date = new Date(period + 'T00:00:00');
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return period;
  }
}

function formatTooltipLabel(period: string, granularity: 'day' | 'week' | 'month'): string {
  if (!period) return '';
  try {
    const date = new Date(period + 'T00:00:00');
    if (granularity === 'week') {
      return `Week of ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
    }
    if (granularity === 'month') {
      return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  } catch {
    return period;
  }
}

const HeartDayRateChart: React.FC<HeartDayRateChartProps> = ({
  childrenData,
  allTimeAverages,
  isDayGranularity,
  granularity,
  startDate,
  endDate,
  onPointClick,
  helperTextDismissed = false,
}) => {
  const theme = useTheme();
  // Compared by value (logoSrc), not object identity — dev-server HMR can give the
  // ThemeProvider's resolved theme and this module's naluV1Theme import different
  // object references after a hot-reload even though both are "the nalu-v1 theme".
  const isNaluV1Theme = theme.logoSrc === naluV1Theme.logoSrc;
  const [highlightedChildId, setHighlightedChildId] = useState<number | null>(null);

  if (childrenData.length === 0) {
    return (
      <Box
        sx={{
          height: 200,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'action.hover',
          // Bare numbers are a multiplier against theme.shape.borderRadius (16 for
          // nalu-v1) under the sx prop, so an explicit px string is required here.
          borderRadius: isNaluV1Theme ? '4px' : 1,
        }}
      >
        <Typography color="text.secondary">No data for this period.</Typography>
      </Box>
    );
  }

  const chartData = buildChartData(childrenData, startDate, endDate, isDayGranularity);

  const handleChartClick = (point: MouseHandlerDataParam) => {
    if (!isDayGranularity || !point?.activeLabel) return;
    onPointClick(point.activeLabel as string);
  };

  return (
    <Box>
      <ResponsiveContainer width="100%" height={220}>
        {/* Recharts primitive/render-prop internals — raw SVG, sx doesn't apply */}
        <LineChart
          data={chartData}
          margin={{ top: 5, right: 16, left: 0, bottom: 20 }}
          onClick={handleChartClick}
          style={{ cursor: isDayGranularity ? 'pointer' : 'default' }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
          <XAxis
            dataKey="period"
            tickFormatter={formatAxisLabel}
            tick={{ fontSize: 11 }}
            tickLine={false}
            angle={-45}
            textAnchor="end"
            height={50}
          />
          <YAxis
            domain={[0, 100]}
            tickFormatter={(v: number) => `${v}%`}
            tick={{ fontSize: 11 }}
            tickLine={false}
            axisLine={false}
            width={40}
          />
          <Tooltip
            formatter={(value: number | string | undefined) => `${value ?? 0}% of days`}
            labelFormatter={(period: string) => formatTooltipLabel(period, granularity)}
          />

          {/* One line per child */}
          {childrenData.map(({ child }) => {
            const childId = (child.id ?? child.childId) as number;
            const color = getChildColor(childId);
            const isHighlighted = highlightedChildId === null || highlightedChildId === childId;
            return (
              <Line
                key={childId}
                type="monotone"
                dataKey={`child_${childId}`}
                name={child.name}
                stroke={color}
                strokeWidth={2}
                strokeOpacity={isHighlighted ? 1 : 0.2}
                dot={{ r: isDayGranularity ? 4 : 3, fill: color, strokeOpacity: isHighlighted ? 1 : 0.2 }}
                activeDot={{ r: isDayGranularity ? 6 : 4 }}
                onMouseEnter={() => setHighlightedChildId(childId)}
                onMouseLeave={() => setHighlightedChildId(null)}
              />
            );
          })}

          {/* Benchmark dotted reference lines — one per child at their all-time average */}
          {childrenData.map(({ child }) => {
            const childId = (child.id ?? child.childId) as number;
            const avg = allTimeAverages[childId];
            if (avg === undefined) return null;
            return (
              <ReferenceLine
                key={`ref-${childId}`}
                y={Math.round(avg * 100)}
                stroke={getChildColor(childId)}
                strokeDasharray="4 4"
                strokeWidth={1.5}
                strokeOpacity={0.6}
              />
            );
          })}
        </LineChart>
      </ResponsiveContainer>

      {/* Metric subtitle — always shown */}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}>
        {granularity === 'day' ? '% of days a heart was earned' : granularity === 'week' ? '% of days a heart was earned (per week)' : '% of days a heart was earned (per month)'}
      </Typography>

      {/* Tap-to-drill-down helper — only shown on day-granularity tabs before dismissed */}
      {isDayGranularity && !helperTextDismissed && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, textAlign: 'center' }}>
          Tap any point to explore that day ↓
        </Typography>
      )}
      {!isDayGranularity && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, textAlign: 'center' }}>
          Switch to Week or Month view to explore individual days.
        </Typography>
      )}
    </Box>
  );
};

export default HeartDayRateChart;
